import axios from "axios";
import Swal from "sweetalert2";
import React, { useState } from "react";
import { motion } from "framer-motion";
import apiCongfig from "../api/apiCongfig";
import Spinner from "../components/spinner/Spinner";
import ReCAPTCHA from "react-google-recaptcha";
import { allServices } from "../components/servicesData"; 

const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  // Transform allServices into dropdown options format
  const servicesOptions = allServices.map(service => ({
    value: service.slug,
    label: service.name.length > 60 
      ? `${service.name.substring(0, 60)}...` 
      : service.name
  }));

  // Add "Others" option
  servicesOptions.push({ value: "OTHERS", label: "Others" });

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.name.trim() || formData.name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters long.";
      isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = "Enter a valid email address.";
      isValid = false;
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    const phone = formData.phone ? formData.phone.replace(/\s+/g, '') : '';
    if (!formData.phone || !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number starting with 6-9.";
      isValid = false;
    }

    if (!formData.service) {
      newErrors.service = "Please select a service.";
      isValid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message cannot be empty.";
      isValid = false;
    }

    if (!recaptchaToken) {
      setRecaptchaError("Please verify that you're not a robot.");
      isValid = false;
    } else {
      setRecaptchaError("");
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (recaptchaError) setRecaptchaError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    const selectedService = servicesOptions.find(
      (s) => s.value === formData.service
    );

    const payload = {
      ...formData,
      service: selectedService?.label || formData.service,
      recaptchaToken,
    };

    try {
      const res = await apiCongfig.post("/contact", payload);

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Form submitted successfully!",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
      });
      setRecaptchaToken(null);
      setErrors({});
      if (window.grecaptcha) window.grecaptcha.reset();
    } catch (err) {
      console.error("Submission error:", err);
      console.error("Server response:", err.response?.data);
      if (err.response?.data?.fields) {
        setErrors(err.response.data.fields);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Please fix the highlighted fields",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: err.response?.data?.error || "Something went wrong. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap -mx-4">
          {/* Left Content Block */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: false }}
            className="w-full lg:w-1/2 px-4 mb-12 lg:mb-0"
          >
            <span className="text-green-600 font-medium mb-2 block">
              Request A Quote
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Need Expert Advice?
            </h2>
            <h3 className="text-xl mb-8">Feel Free to Connect with Us</h3>

            <div className="mb-8">
              <div className="flex items-center mb-4">
                <i className="fa fa-reply text-green-600 text-xl mr-4"></i>
                <span>Prompt reply within hours</span>
              </div>
              <div className="flex items-center">
                <i className="fas fa-phone-alt text-green-600 text-xl mr-4"></i>
                <span>Continued support</span>
              </div>
            </div>

            <p className="mb-8 text-gray-600">
              To give you the best compliance services in India, experienced BIS
              certification consultants at ENVIRONATURE SOLUTION are ready to assist you at every
              stage of certification.
            </p>

            <div className="flex items-center">
              <div className="bg-green-600 text-white p-3 rounded-full mr-4">
                <i className="fas fa-phone-alt text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 mb-1">
                  Call to ask anything about Compliance
                </p>
                <h4 className="text-green-600 text-xl font-bold">
                  +91-9326897902, +91-9322811574
                </h4>
              </div>
            </div>
          </motion.div>

          {/* Right Form Block */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: "easeOut", delay: 1 }}
            viewport={{ once: false }}
            className="w-full lg:w-1/2 px-4"
          >
            <div className="bg-[#bdbbbb] relative p-8 rounded-lg">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    className={`w-full px-4 py-3 rounded bg-white border-none focus:ring-2 focus:ring-green-400 ${
                      errors.name ? "border-red-500" : ""
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    className={`w-full px-4 py-3 rounded bg-white border-none focus:ring-2 focus:ring-green-400 ${
                      errors.email ? "border-red-500" : ""
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your Phone Number"
                    className={`w-full px-4 py-3 rounded bg-white border-none focus:ring-2 focus:ring-green-400 ${
                      errors.phone ? "border-red-500" : ""
                    }`}
                  />
                  {errors.phone && (
                    <p className="text-red-600 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded bg-white border-none focus:ring-2 focus:ring-green-400 ${
                      errors.service ? "border-red-500" : ""
                    }`}
                  >
                    <option value="">--Select Service--</option>
                    {servicesOptions.map((service) => (
                      <option key={service.value} value={service.value}>
                        {service.label}
                      </option>
                    ))}
                  </select>
                  {errors.service && (
                    <p className="text-red-600 text-sm mt-1">{errors.service}</p>
                  )}
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Message here.."
                    className={`w-full px-4 py-3 rounded bg-white border-none focus:ring-2 focus:ring-green-400 ${
                      errors.message ? "border-red-500" : ""
                    }`}
                  ></textarea>
                  {errors.message && (
                    <p className="text-red-600 text-sm mt-1">{errors.message}</p>
                  )}
                </div>

                <div>
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    onErrored={() => { setRecaptchaError("reCAPTCHA failed to load. Please disable any ad blockers.");}}
                    onExpired={() => { setRecaptchaError("reCAPTCHA expired. Please try again."); }}
                    className="mb-4"
                  />
                  {recaptchaError && (
                    <p className="text-red-600 text-sm mt-1">{recaptchaError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center items-center text-white py-3 px-6 rounded-md font-medium transition duration-300 ${
                    loading ? "bg-gray-600 cursor-not-allowed" : "bg-gray-900 hover:bg-gray-800"
                  }`}
                >
                  {loading ? <Spinner /> : "Submit"}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;